import React from 'react';
import { BarChart3 } from 'lucide-react';
import { darkModeClasses, getInfoPanelClasses } from '../../utils/darkMode';
import { useSMSAnalytics } from '../../hooks/useSMSAnalytics';
import { SMSVolumeChart } from '../Charts/SMSVolumeChart';
import { SMSCostTrendsChart } from '../Charts/SMSCostTrendsChart';

interface SMSAnalyticsProps {
  selectedTownId?: string;
}

export const SMSAnalytics: React.FC<SMSAnalyticsProps> = ({ selectedTownId }) => {
  const {
    totalSent,
    totalCost,
    deliveryRate,
    deliveryStatusBreakdown,
    isLoading,
    volumeChartData,
    costChartData,
  } = useSMSAnalytics({ selectedTownId });

  if (isLoading) {
    return (
      <div className={`${darkModeClasses.container} p-6`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm">Loading analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className={`text-lg md:text-xl font-bold ${darkModeClasses.textPrimary} flex items-center`}>
            <BarChart3 className="h-5 w-5 md:h-6 md:w-6 mr-2 text-blue-600" />
            SMS Analytics Dashboard
          </h2>
          <span className={`text-sm ${darkModeClasses.textTertiary}`}>
            Real-time SMS performance metrics
          </span>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${darkModeClasses.container} p-4`}>
          <h3 className={`text-sm font-medium ${darkModeClasses.textTertiary} mb-2`}>
            Total Sent (30 days)
          </h3>
          <p className={`text-2xl font-bold ${darkModeClasses.textPrimary}`}>
            {totalSent.toLocaleString()}
          </p>
          <p className={`text-xs ${darkModeClasses.textTertiary} mt-1`}>
            SMS messages sent
          </p>
        </div>

        <div className={`${darkModeClasses.container} p-4`}>
          <h3 className={`text-sm font-medium ${darkModeClasses.textTertiary} mb-2`}>
            Total Cost
          </h3>
          <p className={`text-2xl font-bold text-red-600`}>
            GHS {totalCost.toFixed(2)}
          </p>
          <p className={`text-xs ${darkModeClasses.textTertiary} mt-1`}>
            Actual costs incurred
          </p>
        </div>

        <div className={`${darkModeClasses.container} p-4`}>
          <h3 className={`text-sm font-medium ${darkModeClasses.textTertiary} mb-2`}>
            Delivery Rate
          </h3>
          <p className={`text-2xl font-bold text-green-600`}>
            {deliveryRate.toFixed(1)}%
          </p>
          <p className={`text-xs ${darkModeClasses.textTertiary} mt-1`}>
            Successful deliveries
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Volume Chart */}
        <div className={`${darkModeClasses.container} p-6`}>
          <SMSVolumeChart data={volumeChartData} />
        </div>

        {/* Cost Trends Chart */}
        <div className={`${darkModeClasses.container} p-6`}>
          <SMSCostTrendsChart data={costChartData} />
        </div>
      </div>

      {/* Delivery Status Breakdown */}
      <div className={`${darkModeClasses.container} p-6`}>
        <div className={`${getInfoPanelClasses('gray')} border rounded-lg p-4`}>
          <h3 className="font-medium mb-2">Delivery Status Breakdown</h3>
          <p className="text-sm mb-3">
            No delivery status data available yet. Status codes will be tracked and displayed here once SMS sending begins using NALO Solutions.
          </p>
          {deliveryStatusBreakdown && deliveryStatusBreakdown.categories.length > 0 && (
            <div className="mt-3">
              <p className="text-sm mb-2"><strong>Raw Data ({deliveryStatusBreakdown.total} total):</strong></p>
              <div className="space-y-1 text-xs">
                {deliveryStatusBreakdown.categories.map((item, index) => (
                  <div key={index} className="flex items-center">
                    <span
                      className="inline-block w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: item.category.color }}
                    />
                    <span>{item.category.name}: {item.count} ({item.percentage.toFixed(1)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 text-xs space-y-1">
            <p><strong>Note:</strong> Interactive pie chart will be enabled once backend data structure matches expected SMSStatusCategory interface.</p>
            <p><strong>Expected Categories:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li className="flex items-center">
                <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                Delivered (000)
              </li>
              <li className="flex items-center">
                <span className="inline-block w-3 h-3 bg-amber-500 rounded-full mr-2"></span>
                Expired/Temporary Issues
              </li>
              <li className="flex items-center">
                <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                Undelivered/Invalid Numbers
              </li>
              <li className="flex items-center">
                <span className="inline-block w-3 h-3 bg-violet-500 rounded-full mr-2"></span>
                Rejected/Content Issues
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
